import createReducer from 'lib/create-reducer'
import { constants } from './actions'
import saga from './sagas'

export const defaultState = {
  mode: 'login',
  // Why the form switched mode on the user's behalf. See SCI-3609.
  modeNotice: null,
  checkoutIntent: null,
  registerStep: 0,
  loginStep: 0,
  name: {
    type: 'text',
    label: 'Name',
    placeholder: 'Enter your name',
    value: ''
  },
  email: {
    type: 'email',
    label: 'Email',
    placeholder: 'name@example.com',
    value: ''
  },
  password: {
    type: 'password',
    label: 'Password',
    placeholder: 'Enter your password',
    value: ''
  },
  passwordPolicy: {
    hasLength: 0,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    satisfied: false
  },
  privacyAgreed: false,
  loading: false,

  userSignupType: null,
  userSignupTypeLoading: false,
  userSignupTypeEmail: null,
  userSignupTypeOrg: null,
  userSignupTypeEmailDomains: null,
  userSignupTypeSamlLoginUrl: null,
  userSignupTypeSamlSsoConfigName: null,
  fetchedSignupType: false,
  fetchedSignupTypeError: false,
  loginError: null
}

const loginModal = createReducer(
  defaultState,
  {
    [constants.SET_MODE]: (state, mode) => ({
      ...state,
      mode: mode,
      // Deliberate switches re-set this with SET_MODE_NOTICE right after.
      modeNotice: null,
      name: {
        ...state.name,
        error: false
      },
      email: {
        ...state.email,
        error: false
      },
      password: {
        ...state.password,
        error: false
      }
    }),
    [constants.SET_MODE_NOTICE]: (state, modeNotice) => ({
      ...state,
      modeNotice: modeNotice || null
    }),
    [constants.SET_CHECKOUT_INTENT]: (state, checkoutIntent) => ({
      ...state,
      checkoutIntent
    }),
    [constants.SET_REGISTER_STEP]: (state, registerStep) => ({
      ...state,
      registerStep: registerStep
    }),
    [constants.SET_LOGIN_STEP]: (state, loginStep) => ({
      ...state,
      loginStep: loginStep
    }),
    [constants.TOGGLE_PRIVACY_AGREED]: state => ({
      ...state,
      privacyAgreed: !state.privacyAgreed
    }),
    [constants.SET_FIELD]: (state, { field, value }, error) => {
      const nextState = {
        ...state,
        [field]: {
          ...state[field],
          value,
          error: !!error,
          errorMessage: error && error.message
        }
      }

      //
      // MILO:
      //       This is also enforced on the backend
      //       but causes some weirdness here if we
      //       don't do it (when logging in after signup)
      if (field === 'email') {
        nextState[field].value = value.trim().toLowerCase()
      }

      return nextState
    },
    [constants.SET_PASSWORD_POLICY]: (state, passwordPolicy) => ({
      ...state,
      passwordPolicy
    }),
    [constants.SUBMIT]: state => ({
      ...state,
      loading: true
    }),
    [constants.SUBMIT_SUCCESS]: state => ({
      ...state,
      password: {
        ...state.password,
        value: ''
      },
      loading: false
    }),
    [constants.SUBMIT_FAILURE]: (state, { field, error } = {}) => {
      const nextState = {
        ...state,
        loading: false
      }

      if (field && error) {
        nextState[field] = {
          ...nextState[field],
          error: true,
          errorMessage: error
        }
      }
      return nextState
    },
    [constants.SET_LOGIN_ERROR]: (state, error) => ({
      ...state,
      loginError: error
    }),
    [constants.FETCH_SIGNUP_TYPE]: state => ({
      ...state,
      userSignupType: null,
      userSignupTypeLoading: true,
      fetchedSignupType: false,
      userSignupTypeEmail: null,
      userSignupTypeOrg: null,
      userSignupTypeEmailDomains: null,
      userSignupTypeSamlLoginUrl: null,
      userSignupTypeSamlSsoConfigName: null
    }),
    [constants.FETCH_SIGNUP_TYPE_SUCCESS]: (
      state,
      {
        userSignupType,
        userSignupTypeOrg,
        userSignupTypeEmailDomains,
        userSignupTypeSamlLoginUrl,
        userSignupTypeSamlSsoConfigName,
        email
      }
    ) => ({
      ...state,
      userSignupType,
      userSignupTypeLoading: false,
      fetchedSignupType: true,
      fetchedSignupTypeError: false,
      userSignupTypeEmail: email,
      userSignupTypeOrg,
      userSignupTypeEmailDomains,
      userSignupTypeSamlLoginUrl: userSignupTypeSamlLoginUrl || null,
      userSignupTypeSamlSsoConfigName: userSignupTypeSamlSsoConfigName || null,
      loginStep: state.mode === 'login' ? 1 : state.loginStep,
      registerStep: ['register', 'saml'].includes(state.mode) ? 1 : state.registerStep
    }),
    [constants.FETCH_SIGNUP_TYPE_FAILURE]: (state, error) => ({
      ...state,
      userSignupType: null,
      userSignupTypeLoading: false,
      fetchedSignupType: false,
      userSignupTypeEmail: null,
      fetchedSignupTypeError: true,
      userSignupTypeOrg: null,
      userSignupTypeEmailDomains: null,
      userSignupTypeSamlLoginUrl: null,
      userSignupTypeSamlSsoConfigName: null,
      email: {
        ...state.email,
        error: state.mode === 'saml',
        errorMessage: state.mode === 'saml' ? 'SSO login is not available for this email.' : ''
      },
      loginStep: state.mode === 'login' ? 0 : state.loginStep,
      // For register mode, advance to step 1 (show password field) on failure
      registerStep: state.mode === 'register' ? 1 : state.registerStep
    }),
    [constants.FLUSH_SIGNUP_TYPE]: state => ({
      ...state,
      userSignupType: null,
      userSignupTypeLoading: false,
      fetchedSignupType: false,
      userSignupTypeEmail: null,
      fetchedSignupTypeError: false,
      userSignupTypeOrg: null,
      userSignupTypeEmailDomains: null
    }),
    [constants.SET_USER_SIGNUP_TYPE]: (state, signupType) => ({
      ...state,
      userSignupType: signupType
    }),
    [constants.SET_USER_SIGNUP_ORG]: (state, signupOrg) => ({
      ...state,
      userSignupTypeOrg: signupOrg
    }),
    [constants.RESET_LOGIN_MODAL_STATE]: state => ({
      ...defaultState
    })
  },
  saga
)

export default loginModal
